import {Component, OnInit, TemplateRef, ViewChild, Input, Output, EventEmitter, AfterViewInit, ChangeDetectorRef} from '@angular/core';
import {Router} from '@angular/router';
import {NgbModal, NgbModalOptions, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import feather from "feather-icons";
import {ToastrService} from 'ngx-toastr';
import {DocumentService} from '../../../../../services/document/document.service';
import {PaiementService} from '../../../../../services/paiement/paiement.service';
import {Paiement} from '../../../../../models/paiement/Paiement';
import {ProductsService} from '../../../../../services/products/products.service';
import {StationService} from '../../../../../services/station.service';
import jsPDF from 'jspdf';
import {PerfectScrollbarDirective} from 'ngx-perfect-scrollbar';

@Component({
  selector: 'app-detail-vente',
  templateUrl: './detail-vente.component.html',
  styleUrls: ['./detail-vente.component.scss']
})
export class
DetailVenteComponent implements OnInit, AfterViewInit {

  @ViewChild('modalLG') modalTemplate!: TemplateRef<any>;
  @ViewChild(PerfectScrollbarDirective, { static: false }) tabScrollRef?: PerfectScrollbarDirective;
  @Input() documentId?: number;
  @Output() downloadDocument = new EventEmitter<{ id: number }>();

  // Données du document
  documentData: any = null;
  isLoading = false;

  tabScrollbarConfig = { suppressScrollX: true, wheelPropagation: false };
  
  // Propriétés affichées
  refExterne: string = '--';
  documentDate: string = '--';
  documentStatut: string = '--';
  netPayer: string = '0.000';
  lignesDocument: any[] = [];
  documentType: string = 'Commande';
  
  // Gestion des onglets
  activeTab: 'articles' | 'paiement' | 'documents' = 'articles';
  
  // Données pour les paiements
  paiements: any[] = [];
  
  // Données pour les documents liés
  documentsLies: any[] = [];

  // Données pour la retenue à la source
  stationData: any = null;
  retenueData: any = null;
  hasRetenueSource: boolean = false;

  // Cache valeur_unitaire (table products) par product_id pour FACTURE FOURNISSEUR (popup détails)
  valeurUnitaireByProductId: Map<number, number> = new Map();

  constructor(
    private modalService: NgbModal,
    private router: Router,
    private documentService: DocumentService,
    private paiementService: PaiementService,
    private productsService: ProductsService,
    private stationService: StationService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }
  
  private modalRef!: NgbModalRef;

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    feather.replace();
  }

  openModal(documentId?: number) {
    // Utiliser le documentId passé en paramètre ou celui de l'Input
    const docId = documentId || this.documentId;
    
    if (!docId) {
      this.toastr.error('ID du document manquant');
      return;
    }

    this.isLoading = true;
    
    // Charger les données depuis l'API
    this.documentService.getDocumentById(docId).subscribe({
      next: (response) => {
        // Extraire le document depuis différentes structures possibles
        this.documentData = response?.data?.document || 
                           response?.data || 
                           response?.document ||
                           response;
        if (this.documentData && response?.data) {
          if (!this.documentData.lignesDocument?.length && !this.documentData.lignes_document?.length) {
            this.documentData.lignesDocument = response.data.lignes_document || response.data.lignesDocument || this.documentData.lignesDocument || [];
          }
        }
        
        // Initialiser les données affichées
        this.initializeData();
        
        this.isLoading = false;
        
        // Ouvrir le modal
        const options: NgbModalOptions = {
          centered: true,
          backdrop: 'static',
          keyboard: true,
          size: 'xl',
          windowClass: 'detail-inventaire-modal-window',
        };
        this.modalRef = this.modalService.open(this.modalTemplate, options);
        
        // Recharger les icônes feather après l'ouverture du modal
        setTimeout(() => {
          feather.replace();
          this.refreshTabScrollbar();
        }, 100);
        
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toastr.error('Erreur lors du chargement du document');
        this.isLoading = false;
      }
    });
  }

  private initializeData() {
    if (!this.documentData) {
      return;
    }

    // Référence externe
    this.refExterne = this.documentData.ref_externe || 
                     (this.documentData as any).reference || 
                     '--';
    
    // Date
    const date = this.documentData.date || 
                (this.documentData as any).date_document;
    if (date) {
      const dateObj = new Date(date);
      this.documentDate = dateObj.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } else {
      this.documentDate = '--';
    }
    
    // Statut
    const statut = (this.documentData as any).statut;
    if (statut) {
      if (Array.isArray(statut) && statut.length > 0) {
        this.documentStatut = statut[0].titre || statut[0].nom || '--';
      } else if (typeof statut === 'object') {
        this.documentStatut = statut.titre || statut.nom || statut.libelle || '--';
      } else if (typeof statut === 'string') {
        this.documentStatut = statut;
      } else {
        this.documentStatut = '--';
      }
    } else {
      this.documentStatut = '--';
    }
    
    // Net à payer
    const netPayerValue = this.documentData.net_payer || 
                        (this.documentData as any).document?.net_payer || 
                        '0';
    this.netPayer = this.formatPrice(netPayerValue);
    
    // Lignes du document
    const lignes = (this.documentData as any).lignesDocument || 
                  (this.documentData as any).lignes_document ||
                  (this.documentData as any).document?.lignesDocument ||
                  (this.documentData as any).document?.lignes_document ||
                  [];
    
    this.lignesDocument = lignes.map((ligne: any) => ({
      libelle: ligne.libelle || 'Sans designation',
      qnt: parseFloat(ligne.qnt || '0'),
      remise: ligne.remise || '0',
      prix_achat: ligne.prix_achat ?? ligne.prixAchat ?? '0',
      prix_ht: parseFloat(ligne.prix_ht || '0'),
      product_id: ligne.product_id != null ? Number(ligne.product_id) : null
    }));
    
    // Type de document (type_document_id ou typeDocument / type_document)
    const typeId = this.documentData.type_document_id;
    const typeMap: { [key: number]: string } = {
      7: 'Devis',
      8: 'Commande',
      11: 'Facture',
      10: 'Bon de livraison',
      9: 'Bon de sortie',
      12: 'Facture fournisseur',
      14: 'FACTURE FOURNISSEUR'
    };
    this.documentType = typeMap[typeId] || 'Commande';
    
    const typeDoc = this.documentData.type_document || this.documentData.typeDocument;
    if (typeDoc) {
      if (typeof typeDoc === 'string') {
        this.documentType = typeDoc;
      } else if (typeDoc.titre) {
        this.documentType = typeDoc.titre;
      } else if (typeDoc.libelle) {
        this.documentType = typeDoc.libelle;
      } else if (typeDoc.nom) {
        this.documentType = typeDoc.nom;
      }
    }
    
    // Charger les paiements
    this.loadPaiements();
    
    // Charger les documents liés
    this.loadDocumentsLies();
    
    // Valeur unitaire produit : uniquement facture client (pas facture fournisseur → colonne prix_achat ligne)
    setTimeout(() => {
      if (this.documentType === 'Facture' || this.documentType === 'FACTURE') {
        this.loadValeurUnitaireForFactureFournisseur();
      }
    }, 0);

  }

  private loadPaiements() {
    const documentId = this.documentData?.id || this.documentId;
    
    // Réinitialiser hasRetenueSource
    this.hasRetenueSource = false;
    
    if (!documentId) {
      this.paiements = [];
      return;
    }
    
    this.paiementService.getPaiementsByDocumentId(documentId).subscribe({
      next: (response: any) => {

        let paiementsData: Paiement[] = [];
        if (Array.isArray(response)) {
          paiementsData = response;
        } else if (response && response.data) {
          if (Array.isArray(response.data)) {
            paiementsData = response.data;
          } else if (response.data.paiements && Array.isArray(response.data.paiements)) {
            paiementsData = response.data.paiements;
          } else if (response.data.paiement && typeof response.data.paiement === 'object') {
            paiementsData = [response.data.paiement];
          }
        } else if (response && response.id) {
          paiementsData = [response];
        }

        // Extraire toutes les lignes de paiement de tous les paiements
        const lignesPaiement: any[] = [];
        paiementsData.forEach((paiement: Paiement) => {
          if (paiement.lignes_paiement && Array.isArray(paiement.lignes_paiement)) {
            paiement.lignes_paiement.forEach((ligne: any) => {
              lignesPaiement.push({
                id: ligne.id,
                date: ligne.date_reglement || paiement.date_paiement || ligne.created_at,
                montant: ligne.montant || '0',
                mode_paiement: this.getModePaiementLabel(ligne.type_paiement),
                reference: this.getReferencePaiement(ligne),
                paiement_id: paiement.id,
                type_paiement: ligne.type_paiement
              });
            });
          }
        });

        this.paiements = lignesPaiement;
        
        // Vérifier si au moins un paiement a une retenue à la source
        this.hasRetenueSource = paiementsData.some((paiement: Paiement) => paiement.retenue_source === true);

        this.cdr.detectChanges();
      },
      error: (error) => {
        this.paiements = this.documentData?.paiements || 
                         this.documentData?.paiement || 
                         [];
        this.cdr.detectChanges();
      }
    });
  }

  private getModePaiementLabel(typePaiement: string): string {
    const labels: { [key: string]: string } = {
      'espece': 'Espèce',
      'cheque': 'Chèque',
      'virement': 'Virement',
      'traite': 'Traite'
    };
    return labels[typePaiement] || typePaiement || '--';
  }

  private getReferencePaiement(ligne: any): string {
    if (ligne.numero_cheque) {
      return `Chèque ${ligne.numero_cheque}`;
    }
    if (ligne.numero_traite) {
      return `Traite ${ligne.numero_traite}`;
    }
    if (ligne.informations_virement) {
      return `Virement ${ligne.informations_virement}`;
    }
    return ligne.reference || '--';
  }

  private loadDocumentsLies() {
    if (!this.documentData) {
      this.documentsLies = [];
      return;
    }

    const documentOriginalId = this.documentData.document_original_id || 
                               (this.documentData as any).document_original_id ||
                               null;
    
    if (!documentOriginalId) {
      this.documentsLies = [];
      this.cdr.detectChanges();
      return;
    }

    
    // Récupérer le document original par son ID
    this.documentService.getDocumentById(documentOriginalId).subscribe({
      next: (response: any) => {
        
        // Extraire le document depuis différentes structures possibles
        const originalDoc = response?.data?.document || 
                           response?.data || 
                           response?.document ||
                           response;
        
        if (!originalDoc || !originalDoc.id) {
          this.documentsLies = [];
          this.cdr.detectChanges();
          return;
        }

        // Déterminer le type de document
        const typeId = originalDoc.type_document_id;
        const typeMap: { [key: number]: string } = {
          7: 'Devis',
          8: 'Commande',
          11: 'Facture',
          10: 'Bon de livraison',
          9: 'Bon de sortie'
        };
        const type = typeMap[typeId] || 'Document';

        // Déterminer le statut - utiliser la même logique que initializeData()
        let statut = '--';
        const docStatut = originalDoc.statut || 
                         originalDoc.statutDocument || 
                         (originalDoc as any).statut_data;
        
        if (docStatut) {
          if (Array.isArray(docStatut) && docStatut.length > 0) {
            statut = docStatut[0].titre || docStatut[0].nom || docStatut[0].libelle || '--';
          } else if (typeof docStatut === 'object' && docStatut !== null) {
            statut = docStatut.titre || docStatut.nom || docStatut.libelle || '--';
          } else if (typeof docStatut === 'string') {
            statut = docStatut;
          }
        }

        if (statut === '--' && originalDoc.statut_id) {
          // On pourrait charger le statut depuis l'API si nécessaire, mais pour l'instant on garde '--'
        }

        this.documentsLies = [{
          id: originalDoc.id,
          type: type,
          reference: originalDoc.ref_externe || originalDoc.reference || '--',
          date: originalDoc.date || originalDoc.date_document || originalDoc.created_at,
          montant: originalDoc.net_payer || originalDoc.total_ttc || '0',
          statut: statut
        }];
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toastr.error('Erreur lors du chargement du document lié');
        this.documentsLies = [];
        this.cdr.detectChanges();
      }
    });
  }


  getDetailProductTableSourceLignes(): any[] {
    if (!this.documentData) {
      return [];
    }
    return (
      (this.documentData as any).lignesDocument ||
      (this.documentData as any).lignes_document ||
      (this.documentData as any).document?.lignesDocument ||
      (this.documentData as any).document?.lignes_document ||
      []
    );
  }

  isFactureClient(): boolean {
    return this.documentType === 'Facture' || this.documentType === 'FACTURE';
  }

  isFactureFournisseur(): boolean {
    return this.documentType === 'Facture fournisseur' || this.documentType === 'FACTURE FOURNISSEUR';
  }

  getDetailFirstPriceLabel(): string {
    return this.isFactureClient() ? 'VALEUR UNITAIRE' : "PRIX D'ACHAT";
  }

  getDetailSecondPriceLabel(): string {
    if (this.isFactureFournisseur()) {
      return 'PRIX HT';
    }
    return 'PRIX DE VENTE';
  }

  private loadValeurUnitaireForFactureFournisseur(): void {
    this.valeurUnitaireByProductId.clear();
    const lignes = (this.documentData as any)?.lignesDocument || (this.documentData as any)?.lignes_document || [];
    if (!Array.isArray(lignes) || lignes.length === 0) {
      return;
    }
    lignes.forEach((ligne: any, index: number) => {
      const productId = ligne.product_id != null ? Number(ligne.product_id) : null;
      if (productId == null || isNaN(productId)) return;
      setTimeout(() => {
        this.productsService.getDetailsProduct(productId).subscribe({
          next: (res: any) => {
            const product = res?.data?.product ?? res?.data ?? res?.product ?? res;
            const rawVal = product?.valeur_unitaire ?? product?.valeurUnitaire;
            const val = rawVal != null && rawVal !== '' ? Number(rawVal) : NaN;
            if (!isNaN(val)) {
              this.valeurUnitaireByProductId.set(productId, val);
              this.cdr.detectChanges();
            }
          },
          error: () => this.cdr.detectChanges()
        });
      }, index * 50);
    });
  }

  getValeurUnitaire(productId: number | null | undefined): number | null {
    if (productId == null) return null;
    const id = Number(productId);
    if (isNaN(id)) return null;
    const val = this.valeurUnitaireByProductId.get(id);
    return val !== undefined ? val : null;
  }

  setActiveTab(tab: 'articles' | 'paiement' | 'documents') {
    this.activeTab = tab;
    this.cdr.detectChanges();
    // Réinitialiser les icônes feather après le changement d'onglet
    setTimeout(() => {
      feather.replace();
      this.cdr.detectChanges();
      this.refreshTabScrollbar();
    }, 100);
  }

  private refreshTabScrollbar(): void {
    setTimeout(() => this.tabScrollRef?.update(), 0);
  }

  editDocumentLie(doc: any): void {
    const id = doc.id;
    if (!id) {
      this.toastr.error('ID du document manquant');
      return;
    }
    const documentStatus = (doc.statut || doc.status || '').toString();
    const typeSlug = this.getTypeSlugForDocumentLie(doc.type);
    const basePath = '/vente';
    const goToApprove = this.shouldRedirectToApproveLie(documentStatus);
    this.modalRef?.close();
    if (goToApprove) {
      this.router.navigate([basePath, typeSlug, 'action', 'validation'], { queryParams: { id } });
    } else {
      this.router.navigate([basePath, typeSlug, 'edit-' + typeSlug], { queryParams: { id } });
    }
  }

  private shouldRedirectToApproveLie(status: string): boolean {
    if (!status) return false;
    const statusesForApprove = [
      'Créé', 'Crée', 'Annulé', 'Négociation', 'Negociation',
      'Accepté', 'Accepte', 'Payé', 'Paye', 'Non payé', 'Non Payé', 'Non paye',
      'Valide', 'Validé', 'Validée', 'Livré', 'Livre', 'Fermé', 'Ferme', 'Reçu', 'Recu'
    ];
    const normalized = this.normalizeStatusLie(status);
    return statusesForApprove.includes(normalized) || statusesForApprove.includes(status);
  }

  private normalizeStatusLie(status: string): string {
    if (!status) return status;
    const statusMap: { [key: string]: string } = {
      tous: 'Tous',
      brouillon: 'Brouillon',
      valide: 'Valide',
      validé: 'Valide',
      validée: 'Valide',
      cree: 'Créé',
      créé: 'Créé',
      annule: 'Annulé',
      annulé: 'Annulé',
      négociation: 'Négociation',
      negociation: 'Négociation',
      fermé: 'Fermé',
      ferme: 'Fermé',
      accepte: 'Accepté',
      accepté: 'Accepté',
      payé: 'Payé',
      paye: 'Payé',
      'non payé': 'Non payé',
      'non paye': 'Non payé',
      'non_payé': 'Non payé',
      'non_paye': 'Non payé',
      livré: 'Livré',
      livre: 'Livré',
      sorti: 'Sorti',
      recu: 'Reçu',
      reçu: 'Reçu'
    };
    const normalized = status.toLowerCase().trim().replace(/\s+/g, ' ');
    return statusMap[normalized] || status;
  }

  private getTypeSlugForDocumentLie(type: string): string {
    const t = (type || '').toLowerCase();
    if (t.includes('devis')) return 'devis';
    if (t.includes('commande') && !t.includes('fournisseur')) return 'commande-client';
    if (t.includes('facture') && !t.includes('fournisseur')) return 'facture';
    if (t.includes('livraison')) return 'bon-livraison';
    if (t.includes('sortie')) return 'bon-sortie';
    return 'devis';
  }

  downloadDocumentLie(doc: any): void {
    const id = doc.id;
    if (!id) {
      this.toastr.error('ID du document manquant pour le téléchargement');
      return;
    }
    this.downloadDocument.emit({ id });
  }

  formatPrice(value: string | number): string {
    const num = typeof value === 'string' ? parseFloat(value) || 0 : value;
    return num.toLocaleString('fr-FR', { 
      minimumFractionDigits: 3, 
      maximumFractionDigits: 3 
    });
  }

  /** Prix d'achat ligne document (aligné approve-vente) ; repli prix_ht. */
  getPrixAchatLigneApprove(ligne: any): number {
    const raw = ligne?.prix_achat ?? ligne?.prixAchat;
    if (raw != null && raw !== '') {
      const n = parseFloat(String(raw));
      if (!isNaN(n)) {
        return n;
      }
    }
    const fb = ligne?.prix_ht;
    if (fb != null && fb !== '') {
      const n2 = parseFloat(String(fb));
      if (!isNaN(n2)) {
        return n2;
      }
    }
    return 0;
  }

  isAchatDetailDocument(): boolean {
    const type = String(this.documentType || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return (
      type.includes('FOURNISSEUR') ||
      type.includes('RECEPTION') ||
      type.includes('INVENTAIRE')
    );
  }

  /** Facture fournisseur : afficher prix_achat de la ligne (repli prix_ht). */
  displayPrixAchatLigne(ligne: any): string {
    const n = this.getPrixAchatLigneApprove(ligne);
    if (n > 0 || ligne?.prix_achat != null || ligne?.prixAchat != null || ligne?.prix_ht != null) {
      return `${this.formatPrice(n)} TND`;
    }
    return '--';
  }

  calculateBenefice(): string {
    if (!this.lignesDocument || this.lignesDocument.length === 0) {
      return '0.000';
    }
    let beneficeTotal = 0;
    this.lignesDocument.forEach((ligne, index) => {
      const valeurUnitaire = this.getValeurUnitaire(ligne.product_id) ?? parseFloat(ligne.prix_achat || '0');
      const prixVente = Number(ligne.prix_ht || 0);
      const quantite = Number(ligne.qnt || 0);
      const remise = parseFloat(ligne.remise || '0') / 100;
      const prixVenteAvecRemise = prixVente * (1 - remise);
      const beneficeLigne = (valeurUnitaire - prixVenteAvecRemise) * quantite;
      beneficeTotal += beneficeLigne;
    });
    return this.formatPrice(beneficeTotal);
  }

  calculateTotalPaiements(): string {
    if (!this.paiements || this.paiements.length === 0) {
      return '0.000';
    }
    
    const total = this.paiements.reduce((sum, paiement) => {
      return sum + parseFloat(paiement.montant || '0');
    }, 0);
    
    return this.formatPrice(total);
  }

  formatDate(dateString: string): string {
    if (!dateString) {
      return '--';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '--';
      }
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return '--';
    }
  }

  generateRetenueSource(): void {
    if (!this.documentData) {
      this.toastr.error('Document non disponible pour générer la retenue à la source');
      return;
    }

    this.toastr.info('Génération du PDF de retenue à la source en cours...', 'Information');

    const stationId = this.documentData.station_id;
    if (!stationId) {
      this.toastr.error('Station ID manquant');
      return;
    }

    this.stationService.getStation(stationId).subscribe({
      next: (response) => {
        if (response && response.success && response.station) {
          this.stationData = response.station;
          this.loadRetenueData();
        } else {
          this.toastr.error('Impossible de charger les données de la station');
        }
      },
      error: (error) => {
        this.toastr.error('Erreur lors du chargement des données de la station');
      }
    });
  }

  private loadRetenueData(): void {
    const documentId = this.documentData?.id || this.documentId;
    
    if (!documentId) {
      this.toastr.error('ID du document manquant');
      return;
    }

    this.paiementService.getPaiementsByDocumentId(documentId).subscribe({
      next: (response: any) => {
        // Normaliser la réponse
        let paiementsData: Paiement[] = [];
        if (Array.isArray(response)) {
          paiementsData = response;
        } else if (response && response.data) {
          if (Array.isArray(response.data)) {
            paiementsData = response.data;
          } else if (response.data.paiements && Array.isArray(response.data.paiements)) {
            paiementsData = response.data.paiements;
          }
        }

        const paiementAvecRetenue = paiementsData.find(p => p.retenue_source === true);
        
        if (!paiementAvecRetenue) {
          this.toastr.warning('Aucune retenue à la source trouvée pour ce document');
          return;
        }

        this.retenueData = {
          montantBrut: paiementAvecRetenue.montant_ttc || this.documentData.total_ttc || '0',
          montantRetenue: paiementAvecRetenue.Montant_RS || '0',
          tauxRetenue: paiementAvecRetenue.Taux_RS || '0',
          dateRetenue: paiementAvecRetenue.date_paiement || this.documentData.date || new Date().toISOString().split('T')[0],
          montantNet: paiementAvecRetenue.net_a_payer || '0'
        };

        // Générer le PDF
        this.generateRetenuePDF();
      },
      error: (error) => {
        // Essayer de générer avec les données du document uniquement
        this.retenueData = {
          montantBrut: this.documentData.total_ttc || '0',
          montantRetenue: '0',
          tauxRetenue: '0',
          dateRetenue: this.documentData.date || new Date().toISOString().split('T')[0],
          montantNet: this.documentData.net_payer || this.documentData.total_ttc || '0'
        };
        this.generateRetenuePDF();
      }
    });
  }

  private generateRetenuePDF(): void {
    if (!this.stationData || !this.documentData) {
      this.toastr.error('Données insuffisantes pour générer le PDF');
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // Couleurs
    const colorDarkR = 0;
    const colorDarkG = 0;
    const colorDarkB = 0;
    const colorLightR = 128;
    const colorLightG = 128;
    const colorLightB = 128;

    // Bordure de la page
    const margin = 5; // Marge de 5mm depuis les bords
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(margin, margin, pageWidth - (margin * 2), pageHeight - (margin * 2), 'D');

    // En-tête - Première ligne : REPUBLIQUE TUNISIENNE (gauche) et CERTIFICAT DE RETENUE D'IMPOT (droite)
    doc.setFontSize(10);
    doc.setTextColor(colorLightR, colorLightG, colorLightB);
    doc.text('REPUBLIQUE TUNISIENNE', 15, yPos);
    
    // Titre principal - Droite, même ligne que REPUBLIQUE TUNISIENNE
    const titleX = pageWidth - 15;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colorDarkR, colorDarkG, colorDarkB);
    doc.text('CERTIFICAT DE RETENUE D\'IMPOT', titleX, yPos, { align: 'right' });
    
    yPos += 5;
    
    // Première ligne de séparation - traverse toute la largeur jusqu'à la bordure
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    const lineY1 = yPos;
    doc.line(margin, lineY1, pageWidth - margin, lineY1);
    yPos += 5;
    
    // Section gauche - MINISTERE
    doc.setFontSize(10);
    doc.setTextColor(colorLightR, colorLightG, colorLightB);
    doc.text('MINISTERE DU PLAN ET DES FINANCES', 15, yPos);
    yPos += 5;
    doc.text('DIRECTION GENERALE', 15, yPos);
    yPos += 5;
    doc.text('DU CONTROLE FISCAL', 15, yPos);
    
    // Suite du titre principal - Droite, aligné à droite, commence après la ligne
    const titleStartY = lineY1 + 5;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colorDarkR, colorDarkG, colorDarkB);
    doc.text('SUR LE REVENU', titleX, titleStartY, { align: 'right' });
    doc.text('OU D\'IMPOT SUR LES SOCIETES', titleX, titleStartY + 7, { align: 'right' });
    
    // Calculer la position Y après les deux sections (gauche et droite)
    const maxY = Math.max(yPos, titleStartY + 14);
    yPos = maxY + 5;
    
    // Deuxième ligne de séparation - traverse toute la largeur jusqu'à la bordure
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Date de retenue - Centrée
    const dateRetenue = this.formatDateForPDF(this.retenueData?.dateRetenue || this.documentData.date);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colorDarkR, colorDarkG, colorDarkB);
    doc.text(`Retenue effectuée le: ${dateRetenue}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Section A - PAYEUR
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('A - PERSONNES OU ORGANISME PAYEUR', 15, yPos);
    yPos += 8;

    // Tableau payeur avec bordures
    const startX = 15;
    const tableWidth = pageWidth - 30;
    const colWidth = tableWidth / 4;
    const rowHeight = 8;
    let xPos = startX;

    doc.setFontSize(9);
    
    // En-têtes du tableau avec fond gris et bordures
    doc.setFillColor(240, 240, 240);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(startX, yPos - 5, tableWidth, rowHeight, 'FD');
    
    // Lignes verticales pour séparer les colonnes dans l'en-tête
    doc.line(startX + colWidth, yPos - 5, startX + colWidth, yPos - 5 + rowHeight);
    doc.line(startX + colWidth * 2, yPos - 5, startX + colWidth * 2, yPos - 5 + rowHeight);
    doc.line(startX + colWidth * 3, yPos - 5, startX + colWidth * 3, yPos - 5 + rowHeight);
    
    doc.setFont('helvetica', 'bold');
    doc.text('MATRICULE FISCAL', xPos + 2, yPos);
    xPos += colWidth;
    doc.text('CODE T.V.A', xPos + 2, yPos);
    xPos += colWidth;
    doc.text('CODE CATÉGORIE', xPos + 2, yPos);
    xPos += colWidth;
    doc.text('N° ETAB. SECONDAIRE', xPos + 2, yPos);
    yPos += rowHeight;

    // Bordures horizontales et verticales pour le tableau
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3); // Épaisseur plus visible
    
    // Dessiner le rectangle complet avec bordures
    doc.rect(startX, yPos - rowHeight - 5, tableWidth, rowHeight * 2, 'D');
    
    // Ligne horizontale au milieu (séparateur entre header et data)
    doc.line(startX, yPos - 5, startX + tableWidth, yPos - 5);
    
    // Lignes verticales pour séparer les colonnes
    doc.line(startX + colWidth, yPos - rowHeight - 5, startX + colWidth, yPos - 5 + rowHeight);
    doc.line(startX + colWidth * 2, yPos - rowHeight - 5, startX + colWidth * 2, yPos - 5 + rowHeight);
    doc.line(startX + colWidth * 3, yPos - rowHeight - 5, startX + colWidth * 3, yPos - 5 + rowHeight);

    // Valeurs payeur
    xPos = startX;
    const matriculePayeur = this.stationData.matricule_fiscale || '--';
    doc.setFont('helvetica', 'bold');
    doc.text(matriculePayeur, xPos + 2, yPos);
    xPos += colWidth;
    doc.text('F', xPos + 2, yPos); // Code TVA par défaut
    xPos += colWidth;
    doc.text('N', xPos + 2, yPos); // Code catégorie par défaut
    xPos += colWidth;
    doc.text('000', xPos + 2, yPos);
    yPos += rowHeight + 5;

    // Dénomination et adresse payeur
    doc.setFont('helvetica', 'normal');
    doc.text('DÉNOMINATION DE LA PERSONNE OU DE L\'ORGANISME PAYEUR:', 15, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    const nomPayeur = this.stationData.nom_entreprise || this.stationData.label || '--';
    doc.text(nomPayeur, 15, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('ADRESSE:', 15, yPos);
    yPos += 5;
    // Adresse simple : region, pays
    const adressePayeur = this.getStationAddress();
    doc.text(adressePayeur, 15, yPos);
    yPos += 12;

    // Ligne de séparation avant Section B - jusqu'à la bordure
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Section B - RETENUES EFFECTUÉES SUR
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('B - RETENU EFFECTUEES SUR:', 15, yPos);
    yPos += 8;

    // Tableau retenues avec bordures
    const tableStartX = 15;
    const labelColWidth = 60; // Largeur pour le label "TOTAL GÉNÉRAL"
    const tableBWidth = pageWidth - 30;
    const dataColWidth = (tableBWidth - labelColWidth) / 3; // Largeur pour chaque colonne de données
    const tableBRowHeight = 8;
    xPos = tableStartX;

    // Formatage des nombres - format français avec virgule pour décimales et espaces pour milliers
    const formatNumber = (value: number): string => {
      // Arrondir à 3 décimales maximum
      const num = Math.round(value * 1000) / 1000;
      // Formater avec virgule comme séparateur décimal
      const parts = num.toString().split('.');
      let integerPart = parts[0];
      const decimalPart = parts[1] || '';
      
      // Ajouter des espaces pour séparer les milliers
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      
      // Si pas de décimales significatives, ne pas afficher la virgule
      if (decimalPart === '' || parseInt(decimalPart) === 0) {
        return integerPart;
      }
      // Afficher avec virgule et jusqu'à 3 décimales
      return integerPart + ',' + decimalPart.padEnd(Math.min(decimalPart.length, 3), '0').substring(0, 3);
    };

    // Calculer les positions Y
    const headerRowY = yPos - 5;
    const dataRowY = headerRowY + tableBRowHeight;
    const totalRowY = dataRowY + tableBRowHeight;

    // En-têtes du tableau avec fond gris clair et bordures noires
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3); // Épaisseur plus visible
    doc.setFillColor(240, 240, 240); // Fond gris clair
    
    // Dessiner le rectangle complet pour les en-têtes
    doc.rect(tableStartX + labelColWidth, headerRowY, dataColWidth * 3, tableBRowHeight, 'FD');
    
    // Lignes verticales pour séparer les colonnes
    doc.line(tableStartX + labelColWidth + dataColWidth, headerRowY, tableStartX + labelColWidth + dataColWidth, headerRowY + tableBRowHeight);
    doc.line(tableStartX + labelColWidth + dataColWidth * 2, headerRowY, tableStartX + labelColWidth + dataColWidth * 2, headerRowY + tableBRowHeight);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0); // Texte noir
    doc.text('Montant brut', tableStartX + labelColWidth + 2, headerRowY + 5, { align: 'left' });
    doc.text('Retenue', tableStartX + labelColWidth + dataColWidth + 2, headerRowY + 5, { align: 'left' });
    doc.text('Montant Net', tableStartX + labelColWidth + dataColWidth * 2 + 2, headerRowY + 5, { align: 'left' });
    
    yPos += tableBRowHeight;

    // Ligne de données - Bordures noires, fond blanc
    doc.setFillColor(255, 255, 255); // Fond blanc
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3); // Épaisseur plus visible
    
    // Dessiner les bordures complètes pour la ligne de données
    doc.rect(tableStartX + labelColWidth, dataRowY, dataColWidth * 3, tableBRowHeight, 'FD');
    
    // Lignes verticales pour séparer les colonnes
    doc.line(tableStartX + labelColWidth + dataColWidth, dataRowY, tableStartX + labelColWidth + dataColWidth, dataRowY + tableBRowHeight);
    doc.line(tableStartX + labelColWidth + dataColWidth * 2, dataRowY, tableStartX + labelColWidth + dataColWidth * 2, dataRowY + tableBRowHeight);

    // Ligne de données (sans label à gauche) - Contenu
    doc.setFont('helvetica', 'normal');
    const montantBrut = formatNumber(parseFloat(this.retenueData?.montantBrut || '0'));
    doc.text(montantBrut, tableStartX + labelColWidth + 2, dataRowY + 5);
    
    const montantRetenue = formatNumber(parseFloat(this.retenueData?.montantRetenue || '0'));
    doc.text(montantRetenue, tableStartX + labelColWidth + dataColWidth + 2, dataRowY + 5);
    
    const montantNet = formatNumber(parseFloat(this.retenueData?.montantNet || '0'));
    doc.text(montantNet, tableStartX + labelColWidth + dataColWidth * 2 + 2, dataRowY + 5);
    
    yPos = dataRowY + tableBRowHeight + 5;

    // Total général avec fond gris légèrement plus foncé et bordures noires
    const totalRowStartY = yPos - 5;
    doc.setFillColor(220, 220, 220); // Fond gris plus foncé
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3); // Épaisseur plus visible
    
    // Dessiner le rectangle complet avec bordures pour le total
    doc.rect(tableStartX, totalRowStartY, tableBWidth, tableBRowHeight, 'FD');
    
    // Lignes verticales pour séparer les colonnes
    doc.line(tableStartX + labelColWidth, totalRowStartY, tableStartX + labelColWidth, totalRowStartY + tableBRowHeight);
    doc.line(tableStartX + labelColWidth + dataColWidth, totalRowStartY, tableStartX + labelColWidth + dataColWidth, totalRowStartY + tableBRowHeight);
    doc.line(tableStartX + labelColWidth + dataColWidth * 2, totalRowStartY, tableStartX + labelColWidth + dataColWidth * 2, totalRowStartY + tableBRowHeight);
    
    // Contenu de la ligne total
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL GÉNÉRAL', tableStartX + 2, yPos);
    doc.text(montantBrut, tableStartX + labelColWidth + 2, yPos);
    doc.text(montantRetenue, tableStartX + labelColWidth + dataColWidth + 2, yPos);
    doc.text(montantNet, tableStartX + labelColWidth + dataColWidth * 2 + 2, yPos);
    yPos += tableBRowHeight + 5;

    // Facture N° (placé après Section B, avant Section C)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`FACTURE N° ${this.refExterne || '--'}`, 15, yPos);
    yPos += 8;

    // Ligne de séparation avant Section C - jusqu'à la bordure
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Section C - BÉNÉFICIAIRE
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('C - BENEFICIAIRE:', 15, yPos);
    yPos += 8;

    // N° de la carte d'identité
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('N° de la carte d\'identité ou sejour pour les étrangers', 15, yPos);
    yPos += 8;

    // Tableau bénéficiaire avec bordures
    const tableCWidth = pageWidth - 30;
    const tableCColWidth = tableCWidth / 4;
    const tableCRowHeight = 8;
    xPos = startX;

    // En-têtes du tableau avec fond gris et bordures
    doc.setFillColor(240, 240, 240);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(startX, yPos - 5, tableCWidth, tableCRowHeight, 'FD');
    
    // Lignes verticales pour séparer les colonnes dans l'en-tête
    doc.line(startX + tableCColWidth, yPos - 5, startX + tableCColWidth, yPos - 5 + tableCRowHeight);
    doc.line(startX + tableCColWidth * 2, yPos - 5, startX + tableCColWidth * 2, yPos - 5 + tableCRowHeight);
    doc.line(startX + tableCColWidth * 3, yPos - 5, startX + tableCColWidth * 3, yPos - 5 + tableCRowHeight);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('MATRICULE FISCAL', xPos + 2, yPos);
    xPos += tableCColWidth;
    doc.text('CODE T.V.A', xPos + 2, yPos);
    xPos += tableCColWidth;
    doc.text('CODE CATÉGORIE', xPos + 2, yPos);
    xPos += tableCColWidth;
    doc.text('N° ETAB. SECONDAIRE', xPos + 2, yPos);
    yPos += tableCRowHeight;

    // Bordures du tableau bénéficiaire
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3); // Épaisseur plus visible
    
    // Dessiner le rectangle complet avec bordures
    doc.rect(startX, yPos - tableCRowHeight - 5, tableCWidth, tableCRowHeight * 2, 'D');
    
    // Ligne horizontale au milieu (séparateur entre header et data)
    doc.line(startX, yPos - 5, startX + tableCWidth, yPos - 5);
    
    // Lignes verticales pour séparer les colonnes
    doc.line(startX + tableCColWidth, yPos - tableCRowHeight - 5, startX + tableCColWidth, yPos - 5 + tableCRowHeight);
    doc.line(startX + tableCColWidth * 2, yPos - tableCRowHeight - 5, startX + tableCColWidth * 2, yPos - 5 + tableCRowHeight);
    doc.line(startX + tableCColWidth * 3, yPos - tableCRowHeight - 5, startX + tableCColWidth * 3, yPos - 5 + tableCRowHeight);

    // Valeurs bénéficiaire - Utiliser les données de la station
    xPos = startX;
    // Utiliser le matricule fiscal de la station comme bénéficiaire
    const matriculeBeneficiaire = this.stationData?.matricule_fiscale || '--';
    doc.setFont('helvetica', 'bold');
    doc.text(matriculeBeneficiaire, xPos + 2, yPos);
    xPos += tableCColWidth;
    doc.text('A', xPos + 2, yPos); // Code TVA par défaut
    xPos += tableCColWidth;
    doc.text('M', xPos + 2, yPos); // Code catégorie par défaut
    xPos += tableCColWidth;
    doc.text('000', xPos + 2, yPos);
    yPos += tableCRowHeight + 5;

    // Nom et adresses bénéficiaire - Utiliser les données de la station
    doc.setFont('helvetica', 'normal');
    doc.text('Nom, prénoms ou raison sociale:', 15, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    // Nom de l'entreprise de la station
    const nomBeneficiaire = this.stationData?.nom_entreprise || this.stationData?.label || '--';
    doc.text(nomBeneficiaire, 15, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('adresse professionnelle:', 15, yPos);
    yPos += 5;
    // Adresse professionnelle : adresses.adresse
    const adressePro = this.getStationProfessionalAddress();
    doc.text(adressePro, 15, yPos);
    yPos += 5;
    doc.text('adresse de résidence:', 15, yPos);
    yPos += 5;
    // Adresse de résidence : adresses.region, adresses.pays
    const adresseRes = this.getStationResidenceAddress();
    doc.text(adresseRes, 15, yPos);
    yPos += 10;

    // Certification
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Je soussigné, certifie exacts les renseignements figurant sur le présent certificat et m\'expose aux sanctions prévues par la loi pour toute inexactitude.', 15, yPos, { maxWidth: pageWidth - 30 });
    yPos += 8;

    // Date et lieu
    const dateCertif = this.formatDateForPDF(new Date().toISOString().split('T')[0]);
    const lieuCertif = this.getStationCity();
    doc.text(`A ${lieuCertif} LE, ${dateCertif}`, 15, yPos);
    yPos += 8;
    doc.text('Cahet et signature du payeur', 15, yPos);

    // Sauvegarder le PDF
    const fileName = `Retenue_Source_${this.refExterne || this.documentData.id}_${dateCertif.replace(/\//g, '_')}.pdf`;
    doc.save(fileName);
    
    this.toastr.success('PDF de retenue à la source généré avec succès', 'Succès');
  }

  private formatDateForPDF(dateString: string): string {
    if (!dateString) return '--';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '--';
    }
  }

  private getStationAddress(): string {
    if (!this.stationData || !this.stationData.adresses || this.stationData.adresses.length === 0) {
      return '--';
    }
    const adresse = this.stationData.adresses[0];
    const parts = [];
    // Seulement region et pays pour l'adresse simple
    if (adresse.region) parts.push(adresse.region);
    if (adresse.pays) parts.push(adresse.pays);
    return parts.length > 0 ? parts.join(', ') : '--';
  }

  private getStationProfessionalAddress(): string {
    if (!this.stationData || !this.stationData.adresses || this.stationData.adresses.length === 0) {
      return 'Adresse non renseignée';
    }
    const adresse = this.stationData.adresses[0];
    return adresse.adresse || 'Adresse non renseignée';
  }

  private getStationResidenceAddress(): string {
    if (!this.stationData || !this.stationData.adresses || this.stationData.adresses.length === 0) {
      return 'Adresse non renseignée';
    }
    const adresse = this.stationData.adresses[0];
    const parts = [];
    if (adresse.region) parts.push(adresse.region);
    if (adresse.pays) parts.push(adresse.pays);
    return parts.length > 0 ? parts.join(', ') : 'Adresse non renseignée';
  }

  private getStationCity(): string {
    if (!this.stationData || !this.stationData.adresses || this.stationData.adresses.length === 0) {
      return 'TUNIS';
    }
    return this.stationData.adresses[0].region || 'TUNIS';
  }

  private getFournisseurAddress(): string {
    const fournisseur = this.documentData?.fornisseur_data || 
                        this.documentData?.fornisseurs?.[0] || 
                        this.documentData?.fournisseur_data || 
                        this.documentData?.fournisseurs?.[0];
    
    if (!fournisseur) {
      return '--';
    }
    
    const adresses = fournisseur.adresses || [];
    if (adresses.length === 0) {
      return '--';
    }
    
    const adresse = adresses[0];
    const parts = [];
    if (adresse.adresse) parts.push(adresse.adresse);
    if (adresse.ville) parts.push(adresse.ville);
    if (adresse.code_postal) parts.push(adresse.code_postal);
    if (adresse.region) parts.push(adresse.region);
    if (adresse.pays) parts.push(adresse.pays);
    
    return parts.length > 0 ? parts.join(', ') : '--';
  }

  private getFournisseurTelephone(): string {
    const fournisseur = this.documentData?.fornisseur_data || 
                        this.documentData?.fornisseurs?.[0] || 
                        this.documentData?.fournisseur_data || 
                        this.documentData?.fournisseurs?.[0];
    
    if (!fournisseur) {
      return '--';
    }
    
    const telephones = fournisseur.telephones || [];
    if (telephones.length === 0) {
      return '--';
    }
    
    return telephones[0].numero || telephones[0].telephone || '--';
  }

}
